type CrawlerPolicy = "metadataOnly" | "sessionless" | "skipAnalytics";

type CrawlerDefinition = {
  userAgent: string;
  policies: readonly CrawlerPolicy[];
  match: "substring" | "product";
};

/**
 * One registry of crawler identities for server rendering, session creation, and
 * client analytics. Each entry opts into its policies explicitly: recognizing a
 * crawler for analytics must not also remove its page body or change its session.
 *
 * Keep this independent of request headers so the browser can use the same
 * identities without varying server-rendered HTML or relying on Cloudflare.
 */
const CRAWLER_REGISTRY: readonly CrawlerDefinition[] = [
  // Meta link previews discard the body. This is the only metadata-only crawler.
  {
    userAgent: "facebookexternalhit",
    policies: ["metadataOnly", "sessionless", "skipAnalytics"],
    match: "substring",
  },
  // These AI crawlers keep the full page body (ENG-8544), but need no session.
  { userAgent: "GPTBot", policies: ["sessionless", "skipAnalytics"], match: "substring" },
  { userAgent: "ClaudeBot", policies: ["sessionless", "skipAnalytics"], match: "substring" },
  { userAgent: "CCBot", policies: ["sessionless", "skipAnalytics"], match: "substring" },
  { userAgent: "Google-Extended", policies: ["sessionless", "skipAnalytics"], match: "substring" },
  {
    userAgent: "Meta-ExternalAgent",
    policies: ["sessionless", "skipAnalytics"],
    match: "substring",
  },
  { userAgent: "Bytespider", policies: ["sessionless", "skipAnalytics"], match: "substring" },
  { userAgent: "TikTokSpider", policies: ["sessionless", "skipAnalytics"], match: "substring" },
  // A robots.txt opt-out token, not a live UA; retained for the existing edge policy.
  {
    userAgent: "Applebot-Extended",
    policies: ["sessionless", "skipAnalytics"],
    match: "substring",
  },
  { userAgent: "PetalBot", policies: ["sessionless", "skipAnalytics"], match: "substring" },
  { userAgent: "Diffbot", policies: ["sessionless", "skipAnalytics"], match: "substring" },
  { userAgent: "Amazonbot", policies: ["sessionless", "skipAnalytics"], match: "substring" },
  { userAgent: "GoogleOther", policies: ["sessionless", "skipAnalytics"], match: "substring" },
  {
    userAgent: "Google-CloudVertexBot",
    policies: ["sessionless", "skipAnalytics"],
    match: "substring",
  },
  // Analytics exclusions only. Preserve the existing full-body and session behavior.
  // Search indexers must never get metadata-only pages: hiding the body risks
  // cloaking penalties and lost organic traffic.
  { userAgent: "Googlebot", policies: ["skipAnalytics"], match: "product" },
  { userAgent: "bingbot", policies: ["skipAnalytics"], match: "product" },
  { userAgent: "DuckDuckBot", policies: ["skipAnalytics"], match: "product" },
  { userAgent: "Applebot", policies: ["skipAnalytics"], match: "product" },
  { userAgent: "AdsBot-Google", policies: ["skipAnalytics"], match: "product" },
  { userAgent: "Mediapartners-Google", policies: ["skipAnalytics"], match: "product" },
  { userAgent: "Meta-WebIndexer", policies: ["skipAnalytics"], match: "product" },
  { userAgent: "Meta-ExternalAds", policies: ["skipAnalytics"], match: "product" },
  { userAgent: "AhrefsBot", policies: ["skipAnalytics"], match: "product" },
  { userAgent: "ReflectionBot", policies: ["skipAnalytics"], match: "product" },
];

const crawlerRules = CRAWLER_REGISTRY.map(({ userAgent, policies, match }) => {
  const normalizedProduct = userAgent.toLowerCase();
  // Preserve the original rendering/session substring matches. New analytics-only
  // entries use product boundaries, including version and variant suffixes.
  const productPattern = new RegExp(
    `(?:^|[\\s;(])${userAgent.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?=$|[/\\s;)\\-])`,
    "i"
  );
  return {
    policies,
    matches: (ua: string) =>
      match === "substring" ? ua.includes(normalizedProduct) : productPattern.test(ua),
  };
});

function matchesPolicy(policy: CrawlerPolicy, userAgent: string | null | undefined): boolean {
  if (!userAgent) return false;
  const normalized = userAgent.toLowerCase();
  return crawlerRules.some((rule) => rule.policies.includes(policy) && rule.matches(normalized));
}

export const isMetadataOnlyCrawlerUserAgent = (userAgent: string | null | undefined) =>
  matchesPolicy("metadataOnly", userAgent);

export const isSessionlessCrawlerUserAgent = (userAgent: string | null | undefined) =>
  matchesPolicy("sessionless", userAgent);

export const isKnownAnalyticsCrawlerUserAgent = (userAgent: string | null | undefined) =>
  matchesPolicy("skipAnalytics", userAgent);
